// Remembers which course version the user opened, so completion goes to that version.
// A Storyline package has a fixed completion URL with the ID of the version it was uploaded for.
// Every newer version copies the same courseLink, so it sends the old ID on completion.

const STORAGE_KEY = 'startedCourses';

/**
 * Reads the started courses map from localStorage
 * @returns {Object<string, {groupRootId: string, packageId: (string|null), startedAt: number}>} The map, or {} when missing or corrupt
 */
const readStartedCourses = () => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        return stored && typeof stored === 'object' ? stored : {};
    } catch (error) {
        console.error('Error reading started courses from localStorage', error);
        return {};
    }
};

/**
 * Writes the started courses map to localStorage
 * @param {Object} params - The function parameters container object
 * @param {Object} params.startedCourses - The map to save
 * @returns {void}
 */
const writeStartedCourses = ({startedCourses}) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(startedCourses));
    } catch (error) {
        console.error('Error writing started courses to localStorage', error);
    }
};

/**
 * Gets the folder ID of a course package from its link
 * e.g. "https://courses.weezbee.co/abc123/story.html" → "abc123"
 * @param {Object} params - The function parameters container object
 * @param {string} [params.courseLink] - The SignDoc courseLink
 * @returns {string|null} The folder ID, or null when there is no link
 */
export const getCourseLinkPackageId = ({courseLink}) => {
    if (!courseLink) {
        return null;
    }
    const pathParts = courseLink.split('/').filter(Boolean);
    return pathParts.length >= 2 ? pathParts[pathParts.length - 2] : null;
};

/**
 * Saves the course version the user just opened
 * @param {Object} params - The function parameters container object
 * @param {Object} params.signDoc - The course SignDoc (id, groupRootId, courseLink)
 * @returns {void}
 */
export const saveStartedCourse = ({signDoc}) => {
    if (!signDoc || !signDoc.id) {
        return;
    }
    const startedCourses = readStartedCourses();
    startedCourses[signDoc.id] = {
        groupRootId: signDoc.groupRootId || signDoc.id,
        packageId: getCourseLinkPackageId({courseLink: signDoc.courseLink}),
        startedAt: Date.now()
    };
    writeStartedCourses({startedCourses});
};

/**
 * Finds the course version the user opened for the ID sent by the course package.
 * A saved course matches when its own ID, its group root ID or its package ID equals the URL ID.
 * When several match, the last opened one wins.
 * @param {Object} params - The function parameters container object
 * @param {string} params.courseIdFromUrl - The SignDoc ID from the completion URL
 * @returns {string} The opened SignDoc ID, or courseIdFromUrl when nothing matches
 */
export const resolveStartedCourseId = ({courseIdFromUrl}) => {
    const startedCourses = readStartedCourses();
    let resolvedId = courseIdFromUrl;
    let latestStartedAt = -1;

    Object.entries(startedCourses).forEach(([signDocId, startedCourse]) => {
        const isMatch = signDocId === courseIdFromUrl ||
            startedCourse?.groupRootId === courseIdFromUrl ||
            startedCourse?.packageId === courseIdFromUrl;
        const startedAt = Number(startedCourse?.startedAt) || 0;
        if (isMatch && startedAt > latestStartedAt) {
            resolvedId = signDocId;
            latestStartedAt = startedAt;
        }
    });

    return resolvedId;
};

/**
 * Removes a course version from the started courses map
 * @param {Object} params - The function parameters container object
 * @param {string} params.signDocId - The SignDoc ID to remove
 * @returns {void}
 */
export const removeStartedCourse = ({signDocId}) => {
    const startedCourses = readStartedCourses();
    if (!startedCourses[signDocId]) {
        return;
    }
    delete startedCourses[signDocId];
    writeStartedCourses({startedCourses});
};
